import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Video, Stethoscope, FileText } from "lucide-react";
import Navbar from "../components/Navbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  });
}

const STATUS_BADGES = {
  upcoming: "bg-accent text-accent-foreground",
  in_progress: "bg-amber-50 text-amber-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
};

export default function DoctorAppointments() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("today");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/doctor/appointments?status=${tab}`);
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main
        data-testid="doctor-appointments-page"
        className="flex-1 px-6 lg:px-10 py-8 lg:py-10 max-w-5xl w-full mx-auto space-y-6"
      >
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">My appointments</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Today, upcoming, past, and cancelled consults.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-secondary p-1 rounded-full h-12 flex-wrap">
            {["today", "upcoming", "completed", "cancelled"].map((t) => (
              <TabsTrigger
                key={t}
                value={t}
                data-testid={`doc-appts-tab-${t}`}
                className="rounded-full px-5 h-10 data-[state=active]:bg-white data-[state=active]:shadow-sm capitalize"
              >
                {t}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={tab} className="mt-6 space-y-3">
            {loading ? (
              [1, 2].map((i) => (
                <div key={i} className="h-24 bg-muted rounded-2xl animate-pulse" />
              ))
            ) : items.length === 0 ? (
              <div
                data-testid="doc-appts-empty"
                className="bg-white border border-border rounded-2xl p-10 text-center"
              >
                <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <div className="font-semibold text-lg mb-1">
                  No {tab} appointments
                </div>
              </div>
            ) : (
              items.map((a) => (
                <div
                  key={a.id}
                  data-testid={`doc-appt-card-${a.id}`}
                  className="bg-white border border-border rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{a.patient?.name}</span>
                      <span
                        className={
                          "text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full " +
                          (STATUS_BADGES[a.status] || "bg-muted text-muted-foreground")
                        }
                      >
                        {a.status}
                      </span>
                      <span className="text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground inline-flex items-center gap-1">
                        {a.mode === "video" ? <Video className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}
                        {a.mode}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{a.patient?.phone}</div>
                    <div className="text-sm mt-1 inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      {fmt(a.scheduled_at)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{a.id} · ₹{a.fee}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(a.status === "upcoming" || a.status === "in_progress") && (
                      <Button
                        data-testid={`doc-appt-start-${a.id}`}
                        onClick={() => navigate(`/doctor/consult/${a.id}`)}
                        className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                      >
                        {a.mode === "video" ? (a.status === "in_progress" ? "Resume consult" : "Start consult") : "Open OPD"}
                      </Button>
                    )}
                    {a.status === "completed" && (
                      <Button
                        variant="outline"
                        data-testid={`doc-appt-view-${a.id}`}
                        onClick={() => navigate(`/doctor/consult/${a.id}`)}
                        className="rounded-full"
                      >
                        <FileText className="h-4 w-4 mr-1.5" /> View Rx
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
